import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { useApp } from "@/lib/store";

type JourneyStep = { number: number; title: string; route: string;
  state: "done" | "blocked" | "next"; detail: string };
type Journey = { workspace: string; steps: JourneyStep[] };

export function WorkspaceJourney({ compact = false }: { compact?: boolean }) {
  const workspace = useApp((state) => state.workspaceId);
  const query = useQuery<Journey>({
    queryKey: ["ontology-workspace-journey", workspace],
    queryFn: () => rawFetch("/ontology/versions/journey/"),
    enabled: Boolean(workspace),
    staleTime: 20_000,
  });
  if (!workspace) return <p className="text-xs text-muted">Select a workspace to see your build path.</p>;
  if (query.isLoading) return <p role="status" className="text-xs text-muted">Checking workspace progress…</p>;
  if (query.isError) return <p role="alert" className="text-xs text-bad">Build path unavailable: {query.error.message}</p>;
  const steps = query.data?.steps ?? [];
  const completed = steps.filter((step) => step.state === "done").length;
  const next = steps.find((step) => step.state === "next") ?? steps.find((step) => step.state === "blocked");
  return <details className="group mb-4 border-y border-border bg-panel/40 px-4 py-2" aria-label="Build and deploy path">
    <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-4 gap-y-1 text-sm focus-visible:outline-accent">
      <strong>From workspace to deployment</strong>
      <span className="text-xs text-muted">{completed}/{steps.length} complete</span>
      <span className="h-1 w-20 bg-border" aria-hidden="true"><span className="block h-full bg-accent" style={{ width: `${steps.length ? completed / steps.length * 100 : 0}%` }} /></span>
      {next && <span className="min-w-0 truncate text-xs text-muted">Next: {next.title}</span>}
      <span className="ml-auto text-xs text-accent group-open:hidden">Show steps ↓</span>
      <span className="ml-auto hidden text-xs text-accent group-open:inline">Hide steps ↑</span>
    </summary>
    <div className={`mt-3 grid gap-2 ${compact ? "sm:grid-cols-3" : "sm:grid-cols-2 xl:grid-cols-3"}`}>
      {steps.map((step) => <Link key={step.number} to={step.route}
        className="flex min-w-0 gap-3 border border-border bg-card/70 p-2.5 hover:border-accent focus-visible:outline-accent">
        <span className="font-mono text-xs text-accent">{String(step.number).padStart(2, "0")}</span>
        <span className="min-w-0"><span className="block text-xs font-medium">{step.title}</span>
          <span className={`mt-0.5 block text-[10px] uppercase tracking-wide ${step.state === "done" ? "text-good" : step.state === "next" ? "text-accent" : "text-muted"}`}>
            {step.state}</span>
          {!compact && <span className="mt-1 block text-[11px] text-muted">{step.detail}</span>}
        </span>
      </Link>)}
    </div>
    <p className="mt-3 text-[11px] text-muted">A signed ORB is a handoff artifact. Customer infrastructure deployment is confirmed outside Ontoligent.</p>
  </details>;
}
